import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShoppingCart, Layers, ArrowRightLeft, Receipt, BarChart3 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import OrdersTab from '@/pages/orders/OrdersTab';
import OrderRatiosTab from '@/pages/orders/OrderRatiosTab';
import ConsolidationPage from '@/pages/ConsolidationPage';
import TransfersPage from '@/pages/TransfersPage';
import RefacturationPage from '@/pages/RefacturationPage';

export default function OrdersPage() {
    const { hasPermission } = useAuth();
    const canViewOrders = hasPermission('orders_view');
    const canViewConsolidation = hasPermission('consolidation_view');
    const canViewTransfers = hasPermission('transfers_view');
    const canViewRefacturation = hasPermission('refacturation_view');
    const canViewOrderStats = hasPermission('orders_stats_view');

    // Onglet actif par défaut : paramètre d'URL (?tab=...) sinon premier accessible
    const [searchParams] = useSearchParams();
    const urlTab = searchParams.get('tab');
    const fallbackTab = canViewOrders
        ? 'orders'
        : canViewConsolidation
            ? 'consolidation'
            : canViewTransfers
                ? 'transfers'
                : canViewRefacturation
                    ? 'refacturation'
                    : 'orders';
    const defaultTab = ['orders', 'consolidation', 'transfers', 'refacturation', 'ratios'].includes(urlTab) ? urlTab : fallbackTab;

    return (
        <div className="animate-fade-in" data-testid="orders-page">
            <Tabs defaultValue={defaultTab} className="w-full">
                <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-3 pb-3 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 shadow-sm mb-4">
                    <div className="flex items-center flex-wrap gap-3">
                        <div className="flex items-center gap-2">
                            <ShoppingCart className="w-5 h-5 text-slate-700" />
                            <h1 className="text-xl font-bold text-slate-900 whitespace-nowrap">Commandes</h1>
                        </div>
                        <TabsList className="bg-white border border-slate-200 h-auto sm:h-9 flex-wrap sm:flex-nowrap gap-1 sm:gap-0 w-full sm:w-auto justify-start">
                            {canViewOrders && (
                                <TabsTrigger value="orders" data-testid="orders-tab" className="h-7 text-xs">
                                    <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />
                                    Commandes
                                </TabsTrigger>
                            )}
                            {canViewConsolidation && (
                                <TabsTrigger value="consolidation" data-testid="consolidation-tab" className="h-7 text-xs">
                                    <Layers className="w-3.5 h-3.5 mr-1.5" />
                                    Consolidation
                                </TabsTrigger>
                            )}
                            {canViewTransfers && (
                                <TabsTrigger value="transfers" data-testid="transfers-tab" className="h-7 text-xs">
                                    <ArrowRightLeft className="w-3.5 h-3.5 mr-1.5" />
                                    Transferts
                                </TabsTrigger>
                            )}
                            {canViewRefacturation && (
                                <TabsTrigger value="refacturation" data-testid="refacturation-tab" className="h-7 text-xs">
                                    <Receipt className="w-3.5 h-3.5 mr-1.5" />
                                    Refacturation
                                </TabsTrigger>
                            )}
                            {canViewOrderStats && (
                                <TabsTrigger value="ratios" data-testid="order-ratios-tab-trigger" className="h-7 text-xs">
                                    <BarChart3 className="w-3.5 h-3.5 mr-1.5" />
                                    Ratios produits
                                </TabsTrigger>
                            )}
                        </TabsList>
                    </div>
                </div>

                {canViewOrders && (
                    <TabsContent value="orders" className="mt-0">
                        <OrdersTab />
                    </TabsContent>
                )}

                {canViewConsolidation && (
                    <TabsContent value="consolidation" className="mt-0">
                        <ConsolidationPage embedded />
                    </TabsContent>
                )}

                {canViewTransfers && (
                    <TabsContent value="transfers" className="mt-0">
                        <TransfersPage embedded />
                    </TabsContent>
                )}

                {canViewRefacturation && (
                    <TabsContent value="refacturation" className="mt-0">
                        <RefacturationPage />
                    </TabsContent>
                )}

                {canViewOrderStats && (
                    <TabsContent value="ratios" className="mt-0">
                        <OrderRatiosTab />
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}
